import type { Reference } from '@apollo/client';
import type { ReactionCount } from '../generated';
import type { TypePolicies } from '../utils/types/TypePolicies';
import { state } from './Comment.state';

export const typePolicies: TypePolicies = {
  Comment: {
    keyFields: (comment) => `Comment:${comment.optimisticId ?? comment.id}`,
    fields: {
      replies: {
        merge(
          existing: Reference[] = [],
          incoming: Reference[] = [],
          { readField }
        ) {
          // Filter incoming comments
          const filteredIncoming = incoming.filter((commentRef: Reference) => {
            const hasAssetId = readField('assetId', commentRef);
            return Boolean(hasAssetId);
          });

          // Filter existing comments too, just in case
          const filteredExisting = existing.filter((commentRef: Reference) => {
            const hasAssetId = readField('assetId', commentRef);
            return Boolean(hasAssetId);
          });

          // Preserve drafts (not yet submitted) so a subscription update
          // doesn't wipe them and collapse the reply composer mid-compose.
          const incomingIds = new Set(
            filteredIncoming.map((ref) => readField<string>('id', ref))
          );
          const draftReplies = existing.filter((commentRef: Reference) => {
            const id = readField<string>('id', commentRef);
            const optimisticId = readField<string>('optimisticId', commentRef);
            const skipSubscriptionUpdate = readField(
              'skipSubscriptionUpdate',
              commentRef
            );
            return (
              id !== undefined &&
              id === optimisticId &&
              skipSubscriptionUpdate === null &&
              !incomingIds.has(id)
            );
          });

          const base = filteredIncoming || filteredExisting || null;
          if (draftReplies.length === 0) {
            return base;
          }
          return [...(base ?? []), ...draftReplies];
        },
      },
      deleted: {
        read: (_, { readField }) => {
          return state.Comment.deleted.get(readField<string>(`id`), false);
        },
      },
      draftId: {
        read: (existing) => {
          return existing || null;
        },
        merge: (existing, incoming) => {
          return existing || incoming || null;
        },
      },
      skipSubscriptionUpdate: {
        read: (existing) => {
          return existing || null;
        },
        merge: (_existing, incoming) => {
          return incoming || null;
        },
      },
      failedToSend: {
        read: (existing) => {
          return existing || null;
        },
        merge: (_existing, incoming) => {
          return incoming || null;
        },
      },
      optimisticId: {
        read: (existing) => {
          return existing || null;
        },
        merge: (existing, incoming) => {
          return existing || incoming || null;
        },
      },
      readByViewer: {
        read: (existing) => {
          return existing || null;
        },
        merge: (existing, incoming) => {
          return existing || incoming || null;
        },
      },
      reactionCounts: {
        merge: (existing, incoming) => {
          const mergedArray: ReactionCount[] = [];
          existing?.forEach((existingItem) => {
            const incomingItem = incoming?.find(
              (item: ReactionCount) => item.shortcode === existingItem.shortcode
            );

            if (incomingItem) {
              mergedArray.push({
                ...existingItem,
                ...incomingItem,
              });
            }
          });

          incoming?.forEach((incomingItem) => {
            const found = existing?.some(
              (item: ReactionCount) => item.shortcode === incomingItem.shortcode
            );
            if (!found) {
              mergedArray?.push({ ...incomingItem });
            }
          });

          return mergedArray;
        },
      },
    },
  },
};
